import { supabase } from "@/lib/supabase";
import type { Tables } from "@synkro/db";

export type StoreSettings = Tables<"store_settings">;

/**
 * Los ajustes de la tienda son una sola fila por organizacion, creada al dar
 * de alta la tienda. Aqui nunca se inserta: si no existe la fila, el problema
 * esta en el alta y esconderlo con un insert lo haria mas dificil de ver.
 */
export async function fetchSettings(organizationId: string): Promise<StoreSettings> {
  const { data, error } = await supabase
    .from("store_settings")
    .select("*")
    .eq("organization_id", organizationId)
    .single();

  if (error) throw error;
  return data;
}

export async function updateSettings(
  organizationId: string,
  cambios: Partial<StoreSettings>,
): Promise<StoreSettings> {
  const { data, error } = await supabase
    .from("store_settings")
    .update(cambios)
    .eq("organization_id", organizationId)
    .select("*")
    .single();

  if (error) throw error;
  return data;
}

/* ----------------------------------------------------------------- ayudas */

/** Un input vacio es null en la base, no cadena vacia. */
export const texto = (v: string): string | null => {
  const limpio = v.trim();
  return limpio === "" ? null : limpio;
};

export const numero = (v: string): number | null => {
  const limpio = v.trim().replace(",", ".");
  if (limpio === "") return null;
  const n = Number(limpio);
  return Number.isFinite(n) ? n : null;
};

/**
 * El dinero viaja como texto en las dos direcciones.
 *
 * `numeric(12,2)` no cabe en el `number` de JavaScript sin perder precision,
 * asi que los tipos generados lo declaran `string`. Convertirlo a Number para
 * pintarlo esta bien; para guardarlo se manda la cadena y que PostgreSQL haga
 * la conversion exacta.
 */
export const dinero = (v: string): string | null => {
  const n = numero(v);
  return n === null ? null : n.toFixed(2);
};

export const comoTexto = (v: unknown): string =>
  v === null || v === undefined ? "" : String(v);

/**
 * Un color de marca tiene que ser un hex valido: se inyecta como variable CSS
 * en la tienda, y un valor con parentesis abre la puerta a inyectar estilos.
 */
export const HEX = /^#[0-9a-fA-F]{6}$/;

export function validarAjustes(borrador: {
  store_name: string;
  email: string;
  whatsapp: string;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
}): Record<string, string> {
  const errores: Record<string, string> = {};

  if (!borrador.store_name.trim()) {
    errores.store_name = "La tienda necesita un nombre.";
  }

  if (borrador.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(borrador.email.trim())) {
    errores.email = "Ese correo no parece válido.";
  }

  // Sin el prefijo del pais, el enlace de WhatsApp no abre desde el extranjero
  // ni desde algunos telefonos. Mas vale avisar aqui que perder la consulta.
  const wa = borrador.whatsapp.trim();
  if (wa && !/^\+?[0-9\s-]{9,15}$/.test(wa)) {
    errores.whatsapp = "Escribe el número con el código de país: +51 987 654 321.";
  }

  for (const campo of ["primary_color", "secondary_color", "accent_color"] as const) {
    const valor = borrador[campo].trim();
    if (valor && !HEX.test(valor)) {
      errores[campo] = "Usa un color en formato #RRGGBB.";
    }
  }

  return errores;
}
